/**
 * ScrambleText Component
 *
 * Creates a smooth "data-decoding" text reveal effect.
 * Gold scrambling characters transition to white final text.
 *
 * Key improvements:
 * - No font switching (prevents size jumping)
 * - Gold-to-white color transition per character
 * - Slower, more deliberate easing
 * - Fixed-width rendering to prevent layout shift
 *
 * Usage: <ScrambleText text="Final Verdict" trigger={isReady} />
 */

import { useState, useEffect, useRef, useMemo } from 'react';
import { cn } from '@/lib/utils';

interface ScrambleTextProps {
  text: string;
  className?: string;
  /** Whether to trigger the scramble animation */
  trigger?: boolean;
  /** Speed of each character reveal in ms (default: 50 = slower/premium) */
  scrambleSpeed?: number;
  /** Delay before starting the animation in ms (default: 0) */
  delay?: number;
  /** If true, will loop the animation. If number, interval between loops in ms */
  loop?: boolean | number;
  /** Callback when animation completes */
  onComplete?: () => void;
  /** Custom color class for the final revealed text (default: 'text-text-primary') */
  finalColor?: string;
}

// Scramble characters - uppercase for "decoding" feel
const GLYPHS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789';

interface CharState {
  char: string;
  isLocked: boolean;
}

export function ScrambleText({
  text,
  className,
  trigger = true,
  scrambleSpeed = 50,
  delay = 0,
  loop = false,
  onComplete,
  finalColor = 'text-text-primary',
}: ScrambleTextProps) {
  const [chars, setChars] = useState<CharState[]>(() =>
    text.split('').map(char => ({ char, isLocked: true }))
  );
  const [, setIsComplete] = useState(false);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const loopTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const hasAnimatedRef = useRef(false);

  // Memoize the final text chars for comparison
  const finalChars = useMemo(() => text.split(''), [text]);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
      if (loopTimeoutRef.current) clearTimeout(loopTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!trigger) {
      setChars(finalChars.map(char => ({ char, isLocked: true })));
      setIsComplete(true);
      return;
    }

    // Only animate once unless loop is enabled
    if (hasAnimatedRef.current && !loop) {
      return;
    }

    const startAnimation = () => {
      hasAnimatedRef.current = true;
      setIsComplete(false);

      // Start with all scrambled (except spaces)
      setChars(finalChars.map(char => ({
        char: char === ' ' ? ' ' : GLYPHS[Math.floor(Math.random() * GLYPHS.length)],
        isLocked: char === ' ', // Spaces are immediately locked
      })));

      let iteration = 0;

      if (intervalRef.current) clearInterval(intervalRef.current);

      intervalRef.current = setInterval(() => {
        setChars(prevChars => {
          const newChars = prevChars.map((state, index) => {
            // If already locked (revealed), keep it
            if (state.isLocked) {
              return { char: finalChars[index], isLocked: true };
            }

            // Characters before current iteration are locked
            if (index < iteration) {
              return { char: finalChars[index], isLocked: true };
            }

            // Still scrambling
            return {
              char: GLYPHS[Math.floor(Math.random() * GLYPHS.length)],
              isLocked: false,
            };
          });

          return newChars;
        });

        // Slower progression (0.5 chars per tick) for premium feel
        iteration += 0.5;

        if (iteration >= finalChars.length) {
          if (intervalRef.current) clearInterval(intervalRef.current);

          // Ensure final state
          setChars(finalChars.map(char => ({ char, isLocked: true })));
          setIsComplete(true);
          onComplete?.();

          // Set up loop if enabled
          if (loop) {
            const loopDelay = typeof loop === 'number' ? loop : 10000;
            loopTimeoutRef.current = setTimeout(() => {
              hasAnimatedRef.current = false;
              startAnimation();
            }, loopDelay);
          }
        }
      }, scrambleSpeed);
    };

    // Start after delay
    const delayTimeout = setTimeout(startAnimation, delay);

    return () => {
      clearTimeout(delayTimeout);
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [trigger, text, scrambleSpeed, delay, loop, finalChars, onComplete]);

  return (
    <span className={cn('inline-block whitespace-nowrap', className)}>
      {chars.map((state, i) => (
        <span
          key={i}
          className={cn(
            'transition-colors duration-300',
            // Locked (revealed) = finalColor, scrambling = gold
            state.isLocked
              ? finalColor
              : 'text-verdict-primary/80'
          )}
        >
          {state.char}
        </span>
      ))}
    </span>
  );
}

